import { createFileRoute } from "@tanstack/react-router";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import {
  Phone,
  Star,
  Users,
  Shield,
  Wifi,
  Music,
  Wine,
  MapPin,
  Clock,
  Award,
  Sparkles,
  PartyPopper,
  Building2,
  Heart,
  Moon,
  CheckCircle2,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FloatingButtons from "@/components/FloatingButtons";
import ContactButtons from "@/components/ContactButtons";
import BrandLine from "@/components/BrandLine";
import TextMariaSection from "@/components/TextMariaSection";
import FamilyChauffeurs from "@/components/FamilyChauffeurs";
import heroImage from "@/assets/sprinter-starlight-blue.jpg?format=webp&quality=72&w=1400&imagetools";

export const Route = createFileRoute("/rentals")({
  head: () => ({
    meta: [
      { title: "Sprinter Limousine Rentals Atlanta | Atlanta Luxury Sprinters" },
      { name: "description", content: "Luxury Mercedes-Benz Sprinter Limousine rentals in Atlanta for corporate events, weddings, and nightlife. Professional chauffeurs included." },
      { property: "og:title", content: "Sprinter Limousine Rentals Atlanta | Atlanta Luxury Sprinters" },
      { property: "og:description", content: "Luxury Mercedes-Benz Sprinter Limousine rentals in Atlanta for corporate events, weddings, and nightlife." },
      { property: "og:url", content: "https://atlantaluxurysprinters.com/rentals" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "canonical", href: "https://atlantaluxurysprinters.com/rentals" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "Mercedes-Benz Sprinter Limousine Rental",
          provider: {
            "@type": "LocalBusiness",
            name: "Atlanta Luxury Sprinters",
            telephone: "+1-678-431-7222",
            address: {
              "@type": "PostalAddress",
              streetAddress: "3401 Norman Berry Dr Suite 146",
              addressLocality: "Atlanta",
              addressRegion: "GA",
              postalCode: "30344",
              addressCountry: "US",
            },
          },
          areaServed: { "@type": "City", name: "Atlanta" },
          description: "Luxury Mercedes-Benz Sprinter Limousine rentals in Atlanta for corporate events, weddings, birthdays, and nightlife with professional chauffeurs.",
          url: "https://atlantaluxurysprinters.com/rentals",
        }),
      },
    ],
  }),
  component: RentalsPage,
});

/* ─── Animated Section Wrapper ─── */
function AnimatedSection({
  children,
  className = "",
  id,
}: {
  children: React.ReactNode;
  className?: string;
  id?: string;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.section
      ref={ref}
      id={id}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7 }}
      className={className}
    >
      {children}
    </motion.section>
  );
}

/* ─── Data ─── */
const sprinterFeatures = [
  { icon: Users, title: "Up to 14 Guests", desc: "Up to 14 in the Limo Style Sprinter or 13 in the Executive Style Sprinter." },
  { icon: Wifi, title: "Onboard Wi-Fi & Entertainment", desc: "Stay connected with high-speed Wi-Fi, premium sound, and ambient lighting." },
  { icon: Wine, title: "Refreshment Bar", desc: "BYOB bar for guests 21+, set with glassware and ice. We do not provide or sell alcohol." },
  { icon: Shield, title: "Professional Chauffeur", desc: "Licensed, vetted, and trained in executive protocol. Your safety and comfort come first." },
];

const partyBusFeatures = [
  { icon: Music, title: "Premium Sound System", desc: "Club-quality audio with Bluetooth connectivity. Your playlist, your vibe." },
  { icon: Sparkles, title: "LED & Starlight Ceilings", desc: "Customizable ambient lighting that transforms the interior into a mobile lounge." },
  { icon: Wine, title: "Full Bar Setup", desc: "Dedicated refreshment area, perfect for toasts, celebrations, and VIP treatment." },
  { icon: Users, title: "Group Capacity", desc: "Spacious layouts accommodating your entire crew in first-class comfort." },
];

const eventTypes = [
  { icon: Building2, title: "Corporate Events", desc: "Impress clients and reward teams with executive-level group transportation to conferences, retreats, and galas." },
  { icon: Heart, title: "Weddings & Celebrations", desc: "Make your special day unforgettable. Elegant Sprinter service for wedding parties, rehearsal dinners, and receptions." },
  { icon: PartyPopper, title: "Birthdays & Milestones", desc: "Celebrate in style, from intimate birthday outings to milestone anniversary celebrations across Atlanta." },
  { icon: Moon, title: "Nightlife & Concerts", desc: "Skip the parking and ride in luxury to Atlanta's top venues, clubs, and concert halls. The party starts onboard." },
];

const serviceAreas = [
  { area: "Buckhead", detail: "Luxury hotels, Phipps Plaza, Lenox Square, and premier nightlife" },
  { area: "Midtown", detail: "Fox Theatre, arts district, and business corridor" },
  { area: "Downtown Atlanta", detail: "State Farm Arena, Mercedes-Benz Stadium, Georgia World Congress Center" },
  { area: "Hartsfield-Jackson Airport", detail: "Domestic and international terminals, seamless group pickups" },
  { area: "Sandy Springs & Dunwoody", detail: "Corporate parks, executive residences, and fine dining" },
  { area: "Alpharetta & Roswell", detail: "Technology corridor, Avalon, and suburban luxury" },
];

const whyUsReasons = [
  { icon: Clock, title: "Always On Time", desc: "Real-time flight tracking, proactive scheduling, and a commitment to punctuality that our clients depend on." },
  { icon: Award, title: "White-Glove Service", desc: "From the first call to the final drop-off, every detail is handled with care, professionalism, and discretion." },
  { icon: Shield, title: "Licensed & Insured", desc: "Fully licensed, commercially insured, and DOT-compliant. Your group travels with total peace of mind." },
  { icon: Star, title: "5-Star Reputation", desc: "Trusted by Atlanta's top executives, celebrities, and event planners. Our reviews speak for themselves." },
];

const testimonials = [
  { name: "Marcus T.", role: "Corporate Event Planner", text: "Atlanta Luxury Sprinters handled transportation for our 60-person corporate retreat flawlessly. Three Sprinters, all on time, impeccable service. Our go-to from now on." },
  { name: "Alicia R.", role: "Bride, Atlanta", text: "Our wedding party Sprinter was absolutely stunning. The starlight ceiling, the champagne, everything was perfect. My bridesmaids are still talking about it." },
  { name: "DeAndre W.", role: "VIP Client", text: "Best nightlife transportation in Atlanta, period. Professional chauffeur, incredible interior, and they handled our entire group without a hitch." },
];

/* ─── Page Component ─── */
function RentalsPage() {
  return (
    <>
      <Header />
      <main>
        {/* ═══ HERO ═══ */}
        <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={heroImage}
              alt="Luxury Sprinter interior with ambient lighting for group transportation in Atlanta"
              width={1920}
              height={1080}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-black/30" />
          </div>

          <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1 }}
            >
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: 60 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="h-[2px] bg-gradient-to-r from-transparent via-purple-light to-transparent mb-8 mx-auto"
              />

              <p className="text-purple-light text-xs font-medium tracking-[0.4em] uppercase mb-6">
                Atlanta's Premier Group Transportation
              </p>

              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-semibold leading-[1.05] mb-8 text-white">
                Luxury Sprinter &{" "}
                <span className="hero-gradient-text">Party Bus Rental</span>{" "}
                in Atlanta
              </h1>

              <p className="text-white/75 text-lg sm:text-xl leading-relaxed mb-10 max-w-2xl mx-auto">
                You've brought everyone together. Let my family handle the driving, with chauffeured
                Mercedes-Benz Sprinters for your work event, wedding, birthday or night out.
              </p>

              <ContactButtons variant="hero" />
              <BrandLine />
            </motion.div>
          </div>
        </section>

        {/* ═══ SPRINTER RENTALS ═══ */}
        <AnimatedSection className="section-padding bg-background">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <div className="accent-line-left mb-6" />
                <p className="text-primary text-sm tracking-[0.3em] uppercase mb-4 font-medium">
                  Flagship Experience
                </p>
                <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-foreground mb-6">
                  Luxury Sprinter Rentals in Atlanta
                </h2>
                <p className="text-muted-foreground text-lg leading-relaxed mb-4">
                  Our Mercedes-Benz Sprinter fleet sets the standard for luxury group
                  transportation in Atlanta. Available in Limousine, Executive Seating,
                  and Jet configurations, each Sprinter delivers a first-class experience
                  that transforms every journey into an event.
                </p>
                <p className="text-muted-foreground leading-relaxed mb-8">
                  Whether you're transporting an executive team to a corporate retreat,
                  celebrating a milestone birthday, or heading to a concert at State Farm
                  Arena, our Sprinters offer the space, style, and sophistication your
                  group deserves.
                </p>
                <a href="#booking" className="btn-primary rounded-sm inline-block">
                  Text Maria About a Sprinter
                </a>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                {sprinterFeatures.map((f, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.1 }}
                    className="luxury-card rounded-lg p-6"
                  >
                    <f.icon className="w-8 h-8 text-primary mb-4" />
                    <h3 className="font-heading text-foreground font-semibold mb-2">{f.title}</h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">{f.desc}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </AnimatedSection>

        {/* ═══ PARTY BUS RENTALS ═══ */}
        <AnimatedSection className="section-padding bg-surface">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <div className="accent-line w-32 mx-auto mb-6" />
              <p className="text-primary text-sm tracking-[0.3em] uppercase mb-4 font-medium">
                Nightlife & Events
              </p>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-foreground mb-6">
                Atlanta Party Bus Rentals
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed max-w-3xl mx-auto">
                Turn your night out into an unforgettable experience. Our party-ready
                Sprinters feature club-quality sound, LED lighting, and premium bar
                setups, all with a professional chauffeur so everyone can enjoy the ride.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              {partyBusFeatures.map((f, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="luxury-card rounded-lg p-6 text-center"
                >
                  <div className="w-14 h-14 rounded-full bg-accent flex items-center justify-center mx-auto mb-4">
                    <f.icon className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="font-heading text-foreground font-semibold mb-2">{f.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{f.desc}</p>
                </motion.div>
              ))}
            </div>

            <div className="text-center">
              <a href="#booking" className="btn-primary rounded-sm inline-block">
                Book Your Party Sprinter
              </a>
            </div>
          </div>
        </AnimatedSection>

        {/* ═══ PERFECT FOR EVERY EVENT ═══ */}
        <AnimatedSection className="section-padding bg-background">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <div className="accent-line w-32 mx-auto mb-6" />
              <p className="text-primary text-sm tracking-[0.3em] uppercase mb-4 font-medium">
                Use Cases
              </p>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-foreground">
                Perfect for Every Occasion
              </h2>
            </div>

            <div className="grid sm:grid-cols-2 gap-8">
              {eventTypes.map((e, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.1 }}
                  className="luxury-card rounded-lg p-8 flex gap-5"
                >
                  <div className="flex-shrink-0 w-12 h-12 rounded-sm bg-accent flex items-center justify-center">
                    <e.icon className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading text-foreground text-xl font-semibold mb-2">{e.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">{e.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </AnimatedSection>

        {/* ═══ LOCAL SEO, SERVICE AREAS ═══ */}
        <AnimatedSection className="section-padding bg-surface">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <div className="accent-line w-32 mx-auto mb-6" />
              <p className="text-primary text-sm tracking-[0.3em] uppercase mb-4 font-medium">
                Coverage
              </p>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-foreground mb-6">
                Serving All of Metro Atlanta
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed max-w-3xl mx-auto">
                From Buckhead to the airport, our luxury Sprinter and party bus rental
                service covers every corner of Atlanta and the surrounding metro area.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {serviceAreas.map((a, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="flex items-start gap-4 luxury-card rounded-lg p-6"
                >
                  <MapPin className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-heading text-foreground font-semibold mb-1">{a.area}</h3>
                    <p className="text-muted-foreground text-sm">{a.detail}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </AnimatedSection>

        {/* ═══ WHY CHOOSE US ═══ */}
        <AnimatedSection className="section-padding bg-background">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <div className="accent-line-left mb-6" />
                <p className="text-primary text-sm tracking-[0.3em] uppercase mb-4 font-medium">
                  The Atlanta Luxury Sprinters Difference
                </p>
                <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-foreground mb-6">
                  Why Atlanta Chooses Us
                </h2>
                <p className="text-muted-foreground text-lg leading-relaxed mb-6">
                  Atlanta Luxury Sprinters isn't just a ride, it's a reputation. Atlanta's most
                  discerning clients trust us because we deliver luxury without
                  compromise, every single time.
                </p>
                <ul className="space-y-3 text-muted-foreground">
                  {["Meticulously maintained Mercedes-Benz Sprinter fleet", "Background-checked, professionally trained chauffeurs", "Transparent pricing, no hidden fees", "24/7 availability for last-minute bookings"].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                {whyUsReasons.map((r, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: 0.15 + i * 0.1 }}
                    className="luxury-card rounded-lg p-6"
                  >
                    <r.icon className="w-8 h-8 text-primary mb-4" />
                    <h3 className="font-heading text-foreground font-semibold mb-2">{r.title}</h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">{r.desc}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </AnimatedSection>

        {/* ═══ TESTIMONIALS ═══ */}
        <AnimatedSection className="section-padding bg-surface">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <div className="accent-line w-32 mx-auto mb-6" />
              <p className="text-primary text-sm tracking-[0.3em] uppercase mb-4 font-medium">
                Client Reviews
              </p>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-foreground">
                What Our Clients Say
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {testimonials.map((t, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.1 }}
                  className="luxury-card rounded-lg p-8"
                >
                  <div className="flex gap-1 mb-4">
                    {Array(5).fill(null).map((_, j) => (
                      <Star key={j} className="w-4 h-4 fill-primary text-primary" />
                    ))}
                  </div>
                  <p className="text-muted-foreground leading-relaxed mb-6 italic">"{t.text}"</p>
                  <div>
                    <p className="font-heading text-foreground font-semibold">{t.name}</p>
                    <p className="text-muted-foreground text-sm">{t.role}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </AnimatedSection>

        {/* ═══ FINAL CTA ═══ */}
        <section className="relative py-24 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={heroImage}
              alt="Luxury Sprinter interior"
              className="w-full h-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/70 to-black/50" />
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="relative z-10 max-w-3xl mx-auto px-4 text-center"
          >
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-white mb-6">
              Tell Me What You're Planning
            </h2>
            <p className="text-white/75 text-lg leading-relaxed mb-10 max-w-2xl mx-auto">
              Text me your date, pickup and group size. I'll check availability myself and send your
              custom quote in writing.
            </p>
            <ContactButtons variant="hero" />
          </motion.div>
        </section>
        <FamilyChauffeurs compact />
        <TextMariaSection />
      </main>
      <Footer />
      <FloatingButtons />
    </>
  );
}
